import { Box, Flex, Tag } from "@chakra-ui/react";
import { useHookstate } from "@hookstate/core";
import { appStore } from "../../../../hookstate/appStore";
import { useSearchBrowseSections } from "../../hooks/useSearchBrowseSections";
import { SectionHeader } from "./SectionHeader";
import { ContentCard } from "../../../../components/cards/ContentCard";
import { ButtonCarousel } from "../../../../components/carousel/ButtonCarousel";
import { sizeEstimator } from "../../../../components/cards/hooks/useGridCardDimensions";
import type { Section } from "../../../../types";

const DYNAMIC_SOURCE_TYPES = [
  "dynamic-comics",
  "dynamic-creators",
  "dynamic-releases",
  "dynamic-bundles",
];

function getItems(section: Section) {
  switch (section.meta.layout.style.context) {
    case "comics":
      return section.comics ?? [];
    case "creators":
      return section.artists ?? [];
    case "releases":
      return section.releases ?? [];
    case "bundle":
      return section.bundles ?? [];
    default:
      return [];
  }
}

interface FocusContentProps {
  cmsContext: string;
  onNavigate: (path: string) => void;
}

export function FocusContent({
  cmsContext,
  onNavigate,
}: FocusContentProps) {
  const { data: sections } = useSearchBrowseSections(cmsContext);

  const init = useHookstate(appStore.init);
  const comicTypes = init.comic_types.get({ noproxy: true });
  const comicGenres = init.comic_genres.get({ noproxy: true });

  const hasSections = sections && sections.length > 0;

  if (!hasSections) return null;

  const renderSection = (section: Section) => {
    switch (section.source_type) {
      case "browse-type-tags":
        return (
          <Flex direction="column" key={section.source_type}>
            <SectionHeader title={section.display_name} />
            <Flex gap={2} flexWrap="wrap">
              {comicTypes?.map((type) => (
                <TagChip
                  key={type.id}
                  label={type.name}
                  onClick={() => onNavigate(`/browse/${type.slug}`)}
                />
              ))}
            </Flex>
          </Flex>
        );

      case "browse-genre-tags":
        return (
          <Flex direction="column" key={section.source_type}>
            <SectionHeader title={section.display_name} />
            <Flex gap={2} overflowX="auto" className="styled-scrollbar-hover" pb={1}>
              {comicGenres?.slice(0, 6).map((genre) => (
                <TagChip
                  key={genre.id}
                  label={genre.name}
                  onClick={() => onNavigate(`/browse/${genre.slug}`)}
                />
              ))}
            </Flex>
          </Flex>
        );

      default: {
        if (!DYNAMIC_SOURCE_TYPES.includes(section.source_type)) return null;

        const items = getItems(section);
        if (!items.length) return null;

        const { context, mode, size } = section.meta.layout.style;
        const { width: cardWidth } = sizeEstimator(context, mode, size);
        const showViewAll = section.meta.layout.display?.include_view_all;
        return (
          <Flex direction="column" key={section.source_type}>
            <SectionHeader
              title={section.display_name}
              viewAllLabel={showViewAll ? "View all >" : undefined}
              onViewAll={showViewAll ? () => onNavigate(`/cms/${section.uuid}`) : undefined}
            />
            <ButtonCarousel
              width="100%"
              containerProps={{ gap: 3 }}
              includeButtons={false}
            >
              {items.map((item, index) => (
                <Box
                  key={item.id}
                  flexShrink={0}
                  w={mode === "grid" && cardWidth ? `${cardWidth}px` : undefined}
                >
                  <ContentCard
                    content={item}
                    layout={section.meta.layout}
                    section={section}
                    index={index}
                  />
                </Box>
              ))}
            </ButtonCarousel>
          </Flex>
        );
      }
    }
  };

  return (
    <Flex direction="column" gap={4} p={5}>
      {sections?.map((section: Section, i: number) => {
        const content = renderSection(section);
        if (!content) return null;
        return (
          <Flex direction="column" key={section.source_type} gap={4}>
            {i > 0 && <Box h="1px" bg="transparent.white.10" />}
            {content}
          </Flex>
        );
      })}
    </Flex>
  );
}

function TagChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Tag
      bg="transparent.white.10"
      borderRadius="full"
      px="12px"
      py="8px"
      fontSize="14px"
      fontWeight="500"
      fontFamily="Roboto"
      color="neutral.100"
      letterSpacing="0.5px"
      cursor="pointer"
      _hover={{ bg: "transparent.white.20" }}
      onClick={onClick}
    >
      {label}
    </Tag>
  );
}
